import type { AdminVehicle } from '@ecowheels/types';
import { VehicleStatus } from '@ecowheels/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { Badge, Button, Card, ConfirmButton, PageHeader, QueryState } from '@/components/ui';
import {
  FUEL_LABELS,
  STATUS_LABELS,
  STATUS_TONE,
  formatMileage,
  formatPrice,
  publicVehicleUrl,
  vehicleTitle,
} from '@/lib/vehicle-labels';

export function VehiclesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.get<AdminVehicle[]>('/admin/vehicles'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/vehicles/${id}`),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (e: ApiError) => setError(e.message),
  });

  const vehicles = data ?? [];

  return (
    <>
      <PageHeader
        title="Használt autók"
        action={<Button onClick={() => navigate('/vehicles/new')}>Új jármű</Button>}
      />

      {error && (
        <p role="alert" className="mb-4 text-sm text-red-400">
          {error}
        </p>
      )}

      <QueryState
        isLoading={isLoading}
        error={loadError}
        isEmpty={vehicles.length === 0}
        emptyText="Még nincs feltöltött jármű. Kezdje az „Új jármű” gombbal."
      >
        <ul className="space-y-3">
          {vehicles.map((vehicle) => (
            <li key={vehicle.id}>
              <Card className="flex flex-col gap-4 p-3 sm:flex-row sm:items-center">
                {/* A car with no photos yet is normal — it's the state right
                    after creating one — so this is a labelled placeholder
                    rather than a broken image. */}
                {vehicle.coverImage ? (
                  <img
                    src={vehicle.coverImage.url}
                    alt={vehicle.coverImage.alt}
                    className="h-24 w-full rounded bg-white/5 object-cover sm:w-36"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-24 w-full shrink-0 items-center justify-center rounded bg-white/5 text-xs text-white/30 sm:w-36">
                    nincs kép
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/vehicles/${vehicle.id}`}
                      className="truncate font-medium hover:text-brand"
                    >
                      {vehicleTitle(vehicle)}
                    </Link>
                    <Badge tone={STATUS_TONE[vehicle.status]}>
                      {STATUS_LABELS[vehicle.status]}
                    </Badge>
                  </div>

                  <p className="mt-1 text-sm text-white/60">
                    {formatPrice(vehicle.priceHuf)}
                    {vehicle.vatDeductible && ' · ÁFÁ-s'} · {formatMileage(vehicle.mileageKm)} ·{' '}
                    {FUEL_LABELS[vehicle.fuelType]}
                  </p>

                  <p className="mt-1 text-xs text-white/35">
                    {vehicle.imageCount} kép · /{vehicle.slug}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2">
                  {/* Only reachable where the public page actually exists — a
                      draft has no URL to open, and linking to a 404 from the
                      admin is how you learn to distrust the admin. */}
                  {vehicle.status !== VehicleStatus.DRAFT && (
                    <a
                      href={publicVehicleUrl(vehicle.slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/5"
                    >
                      Megnyitás
                    </a>
                  )}
                  <Button variant="ghost" onClick={() => navigate(`/vehicles/${vehicle.id}`)}>
                    Szerkesztés
                  </Button>
                  <ConfirmButton
                    onConfirm={() => remove.mutate(vehicle.id)}
                    confirmText={`Biztosan törli a(z) "${vehicleTitle(vehicle)}" hirdetést? A képei is törlődnek.`}
                  >
                    Törlés
                  </ConfirmButton>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}
